import './item.comp.css'

import React, { useCallback, useMemo, useRef } from 'react'
import { useSessionStorage } from 'react-use'
import clsx from 'clsx'
import { Descendant } from 'slate'

import {
  ColumnConditionValue,
  ConditionValue,
  thousandFmtNoDecimal,
  TypographyGroupValue,
  TypographySelectValue,
} from '@makyoapp/makyo-common'
import {
  BaseComponent,
  Button,
  ColorVariant,
  CornerRadius,
  getShapeClass,
  getTextColorStyles,
  getTypographyTokenStyle,
  MaterialIcon,
  RawSpacingInput,
  RenderText,
  SearchBar,
  Skeleton,
  SkeletonGroup,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { cn } from '@/lib/cn'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey, UserColumnFilterValue } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { MAX_ROWS_LIMIT } from '@/shared/constants'
import { useDebounceInput } from '@/shared/makyo-hooks'
import { getSafely } from '@/shared/safely'

import { useCompPagination } from '../../hooks/useCompPagination'
import { CompFilterV2 } from '../../shared/CompFilterV2'
import { CompHeadline } from '../../shared/CompHeadline'
import { CompPagination } from '../../shared/CompPagination'
import { BaseCompCardProps } from './comp'

export interface CompCardWrapperCoreProps {
  infos: CoreComponentInfos
  tableData: CoreComponentTableData
  action: CoreComponentAction
  children: (props: BaseCompCardProps) => React.ReactNode
}

/**
 * CompCardWrapperCore
 */
export const CompCardWrapperCore = React.memo(
  ({
    infos: { component, settings, containerClient, detailRowIndex, detailTableId, currentParentBg },
    tableData: { dbTable: table, language, userSpecific },
    action: { simulateAction },
    children,
  }: CompCardWrapperCoreProps) => {
    const { checkVisibility } = useCheckVisibilityV2()

    //
    const compId = component._id
    const wrapperRef = useRef<HTMLDivElement>(null)

    const orientation = settings[ComponentSettingKey.CardDesignOrientation]
    const isHoriz = orientation === 'horizontal'

    // ========================= data =========================

    const datasetLimit = settings[ComponentSettingKey.CardDataNumItems]
    const isLimited = datasetLimit?.enabled
    const limit = datasetLimit?.value ?? 0
    const maxPerItems = isLimited ? limit : MAX_ROWS_LIMIT

    const columnsCount = (settings[ComponentSettingKey.CardDesignNumCols] as number) ?? 1
    const numRows = (settings[ComponentSettingKey.CardDesignNumRows] as number) ?? 1

    // pagination state
    const { pagination, setPagination } = useCompPagination({
      compId,
      defaultPerPage: isHoriz ? maxPerItems : Math.min(columnsCount * numRows, maxPerItems),
    })

    // data filter & sort settings
    const filterSetting = settings[ComponentSettingKey.CardDataFilter] as ColumnConditionValue | undefined
    const sortType = settings[ComponentSettingKey.CardDataSortType] as string
    const sortBy = settings[ComponentSettingKey.CardDataSortBy] as string

    // search state
    const [debouncedSearch, , , currentSearch, setCurrentSearch] = useDebounceInput('')

    // user filter state
    const [userFilter, setUserFilter] = useSessionStorage(`userFilter-${compId}`, {})

    // get initial rows
    const [{ rows, filteredRowsCount, allRowsCount }, isLoading] = useCompTableData({
      table,
      componentId: component._id,
      pagination,
      filterSetting,
      sortSetting: { sortType, sortBy },
      search: debouncedSearch,
      userFilter,
    })

    // create a stable row indexes map
    // todo: use state instead
    const rowIndexesMap = useMemo(() => {
      return table.GetRowIndexesMap(language, userSpecific)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, table, userSpecific, filteredRowsCount]) // allRowsCount is needed to trigger re-render

    const totalPages = useMemo(() => {
      return Math.ceil(filteredRowsCount / pagination.perPage)
    }, [filteredRowsCount, pagination.perPage])

    // user (manual) filter settings
    const userFilterSetting: Array<UserColumnFilterValue> = useMemo(() => {
      const stg = settings[ComponentSettingKey.CardBehaviorUserFilter] as UserColumnFilterValue[]
      return Array.isArray(stg) ? stg : []
    }, [settings])

    // groupBy
    const groupBy = settings[ComponentSettingKey.CardDataGroupBy] as string

    // map groupBy to entries using object.groupBy
    const groupEntries: [string, IRow[]][] | undefined = useMemo(() => {
      if (!groupBy) return
      const groupByCol = table.columnsMap.get(groupBy)
      if (!groupByCol) return

      return Object.entries(Object.groupBy(rows, (item) => item[groupByCol._id].toString()) as Record<string, IRow[]>)
      //
    }, [groupBy, rows, table.columnsMap])

    // ========================= user filter (hidden) =========================

    // ========================= design =========================

    // base
    const width = getSafely(settings, [ComponentSettingKey.CardDesignWidth], ['100%'])[0]
    const alignment = settings[ComponentSettingKey.CardDesignCompHAlign] ?? 'parent'
    const margin = settings[ComponentSettingKey.CardDesignMargin] as RawSpacingInput
    const padding = settings[ComponentSettingKey.CardDesignPadding] as RawSpacingInput

    // search box
    const searchAlign = settings[ComponentSettingKey.CardDesignSearchAlignment] ?? 'center'
    const searchBgColor = settings?.[ComponentSettingKey.CardDesignSearchBgColor] ?? 'primary-90'
    const searchCornerRadius = settings[ComponentSettingKey.CardDesignSearchCorner] as CornerRadius
    const searchMargin = settings[ComponentSettingKey.CardDesignSearchMargin] as RawSpacingInput
    const searchWidth = settings[ComponentSettingKey.CardDesignSearchBox]?.[0] ?? '100%'
    const searchWidthValue = Number(String(searchWidth).replace(/[^0-9.]/g, '')) || 100
    const searchWidthUnit = String(searchWidth).replace(/[0-9.]/g, '') || '%'

    // filter
    const filterOverlayBgColor = settings[ComponentSettingKey.CardDesignFilterBgColor] ?? 'primary-95'
    const filterChipColor = settings[ComponentSettingKey.CardDesignFilterChipColor] ?? 'primary-95'
    const filterCornerRadius = settings[ComponentSettingKey.CardDesignFilterCornerRadius] as CornerRadius
    const filterPosition = settings[ComponentSettingKey.CardBehaviorUserFilterPosition] ?? 'top'

    const paginationAlign = settings[ComponentSettingKey.CardDesignPaginationAlignment] ?? 'center'
    const showPagination = settings[ComponentSettingKey.CardDesignPagination]

    // typography
    const typoSettings = settings[ComponentSettingKey.CardDesignTypography] as TypographyGroupValue
    const supportingTextTypo = typoSettings[ComponentSettingKey.CardDesignSupportingTextTypography] ?? {
      typography: 'Label Small',
    }

    // group header
    const groupHeaderTypo = getSafely(typoSettings, [ComponentSettingKey.CardDesignGroupHeaderTypography], {
      typography: 'Title Medium',
    } as TypographySelectValue)
    const groupHeaderTypoStyle = getTypographyTokenStyle(groupHeaderTypo)
    const groupHeaderAlign = settings[ComponentSettingKey.CardDesignGroupHeaderAlign] || 'center'
    const groupHeaderMargin = parseSpacing(settings[ComponentSettingKey.CardDesignGroupHeaderMargin])

    // const itemVariant = settings[ComponentSettingKey.CardDesignVariant] as CardVariant

    // add button
    const addButtonColor = settings[ComponentSettingKey.CardDesignAddButtonColor] ?? 'primary-20'
    const addButtonCornerRadius = settings[ComponentSettingKey.CardDesignAddButtonCornerRadius] as CornerRadius
    const addButtonPosition = settings[ComponentSettingKey.CardBehaviorAddButtonPosition] ?? 'inline'

    // ========================= component spacing =========================

    // Helper function to format spacing value
    const formatSpacingValue = (value?: string | number | { unit?: string; value?: number }) => {
      if (!value) return undefined
      if (typeof value === 'number') return `${value}px`
      if (typeof value === 'string') return value
      if (typeof value === 'object' && 'value' in value && 'unit' in value) {
        return `${value.value}${value.unit}`
      }
      return undefined
    }

    // Helper function to get component spacing value
    const getComponentSpacingValue = (key: ComponentSettingKey, fallback?: string | number) => {
      const providedValue = settings[key] !== undefined ? settings[key] : fallback
      return formatSpacingValue(providedValue) || formatSpacingValue(fallback)
    }

    // Get all spacing values
    const headerSpacing = getComponentSpacingValue(ComponentSettingKey.CardDesignHeaderSpacing, '0.5rem')
    const imageSpacing = getComponentSpacingValue(ComponentSettingKey.CardDesignImageSpacing, '0.5rem')
    const footerSectionSpacing = getComponentSpacingValue(ComponentSettingKey.CardDesignFooterSectionSpacing, '0.5rem')
    const footerTitleSpacing = getComponentSpacingValue(ComponentSettingKey.CardDesignFooterTitleSpacing, '0.5rem')
    const contentTitleSpacing = getComponentSpacingValue(ComponentSettingKey.CardDesignContentTitleSpacing, '0.5rem')
    const contentSubtitleSpacing = getComponentSpacingValue(
      ComponentSettingKey.CardDesignContentSubtitleSpacing,
      '0.5rem'
    )
    const supportingTextSpacing = getComponentSpacingValue(
      ComponentSettingKey.CardDesignSupportingTextSpacing,
      '0.5rem'
    )
    const metadataSpacing = getComponentSpacingValue(ComponentSettingKey.CardDesignMetadataSpacing, '0.5rem')

    // ========================= actions =========================

    const handleAddButtonClick = () => {
      simulateAction({
        actions: settings[ComponentSettingKey.CardActionAddButtonClick] ?? [],
        tableId: table.id,
      })
    }

    // ========================= behavior =========================
    const withSearch = settings[ComponentSettingKey.CardBehaviorUserSearch] as boolean
    const withSearchResultsInfo = settings[ComponentSettingKey.CardBehaviorSearchResultsInfo] as boolean

    const withAddButton = settings[ComponentSettingKey.CardBehaviorAddButtonShow] as boolean
    const addButtonVisibility = settings[ComponentSettingKey.CardBehaviorAddButtonVisibility] as
      | ConditionValue
      | undefined

    const showAddButton =
      withAddButton &&
      checkVisibility({
        visibility: addButtonVisibility,
        layerTableId: table.id,
      })

    // ========================= element =========================

    // ========================= component title / headline =========================

    const headline = (settings[ComponentSettingKey.CardDataCompTitle] as Descendant[]) ?? []
    const headlineMargin = settings[ComponentSettingKey.CardDesignHeadlineMargin]
    const headlineTypo = typoSettings[ComponentSettingKey.CardDesignHeadlineTypography]
    const headlineAlign = getSafely(settings, [ComponentSettingKey.CardDesignCompTitleAlign], 'center')

    // color
    // comp title
    const compTitleColor = settings[ComponentSettingKey.CardDesignCompTitleColor]
    const compTitleColorSolid =
      (settings[ComponentSettingKey.CardDesignCompTitleColorSolid] as ColorVariant) || 'primary-40'
    const compTitleColorStartGradient =
      (settings[ComponentSettingKey.CardDesignCompTitleColorStartGradient] as ColorVariant) || 'primary-40'
    const compTitleColorEndGradient =
      (settings[ComponentSettingKey.CardDesignCompTitleColorEndGradient] as ColorVariant) || 'primary-40'
    const compTitleColorGradientDirection = settings[ComponentSettingKey.CardDesignCompTitleColorGradientDirection]

    // group header
    const groupHeaderColor = settings[ComponentSettingKey.CardDesignGroupHeaderColor]
    const groupHeaderColorSolid =
      (settings[ComponentSettingKey.CardDesignGroupHeaderColorSolid] as ColorVariant) || 'primary-40'
    const groupHeaderColorStartGradient =
      (settings[ComponentSettingKey.CardDesignGroupHeaderColorStartGradient] as ColorVariant) || 'primary-40'
    const groupHeaderColorEndGradient =
      (settings[ComponentSettingKey.CardDesignGroupHeaderColorEndGradient] as ColorVariant) || 'primary-40'
    const groupHeaderColorGradientDirection = settings[ComponentSettingKey.CardDesignGroupHeaderColorGradientDirection]

    // ========================= render =========================

    // Memoize the card props function to prevent unnecessary function creation
    const getCompCardProps = useCallback(
      (items: IRow[]): BaseCompCardProps => {
        return {
          rowIndexesMap,
          items,
          table,
          settings: {
            ...settings,
            componentSpacing: {
              header: headerSpacing,
              image: imageSpacing,
              footer: footerSectionSpacing,
              footerTitle: footerTitleSpacing,
              contentTitle: contentTitleSpacing,
              contentSubtitle: contentSubtitleSpacing,
              contentSupportingText: supportingTextSpacing,
              metadata: metadataSpacing,
            },
          },
        }
      },
      [
        table,
        settings,
        rowIndexesMap,
        headerSpacing,
        imageSpacing,
        footerSectionSpacing,
        footerTitleSpacing,
        contentTitleSpacing,
        contentSubtitleSpacing,
        supportingTextSpacing,
        metadataSpacing,
      ]
    )

    // if there are no rows after filtering, don't show the component
    const noDataToShow = !!filterSetting && allRowsCount === 0
    if (noDataToShow) return null

    const showSearchResultsInfo = withSearchResultsInfo && !isLoading && withSearch && debouncedSearch.trim() !== ''

    return (
      <BaseComponent
        id={compId}
        ref={wrapperRef}
        width={width}
        alignment={alignment}
        margin={margin}
        backgroundColor={currentParentBg}
        className={clsx('box-border flex flex-col', isHoriz ? 'overflow-x-auto' : 'overflow-x-visible')}
      >
        <CompHeadline
          containerClient={containerClient}
          detailRowIndex={detailRowIndex}
          detailTableId={detailTableId}
          headline={headline}
          headlineMargin={headlineMargin}
          headlineTypo={headlineTypo}
          compTitleAlign={headlineAlign}
          colorType={compTitleColor}
          backgroundColor={currentParentBg}
          styles={{
            ...getTextColorStyles(
              compTitleColor,
              compTitleColorSolid,
              compTitleColorStartGradient,
              compTitleColorEndGradient,
              compTitleColorGradientDirection,
              currentParentBg ?? 'primary-90'
            ),
          }}
        />

        {filterPosition === 'top' && (
          <CompFilterV2
            id={compId}
            value={userFilter}
            setValue={setUserFilter}
            settings={userFilterSetting}
            table={table}
            filterChipColor={filterChipColor}
            filterOverlayBgColor={filterOverlayBgColor}
            filterCornerRadius={filterCornerRadius}
            filterMargin={settings[ComponentSettingKey.CardDesignFilterMargin]}
            inline={false}
          />
        )}

        <div className='flex items-center gap-2 mb-4'>
          {withSearch && (
            <SearchBar
              value={currentSearch}
              onChange={(e) => {
                setCurrentSearch(e.target.value)
                setPagination(0, pagination.perPage)
              }}
              onClear={() => {
                setCurrentSearch('')
              }}
              backgroundColor={searchBgColor}
              customWidth={{
                type: searchWidthUnit === '%' ? 'percentage' : 'pixel',
                value: searchWidthValue,
              }}
              // margin={searchMargin}
              borderStyles={{
                radius: searchCornerRadius,
              }}
              align={searchAlign}
              inputClassName='py-2.5'
            />
          )}

          <div className='flex min-w-0 items-center gap-1.5'>
            {filterPosition === 'inline' && (
              <CompFilterV2
                id={compId}
                value={userFilter}
                setValue={setUserFilter}
                settings={userFilterSetting}
                table={table}
                filterChipColor={filterChipColor}
                filterOverlayBgColor={filterOverlayBgColor}
                filterCornerRadius={filterCornerRadius}
                filterMargin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                inline={true}
              />
            )}

            {showAddButton && addButtonPosition === 'inline' && (
              <button
                className={clsx(
                  'p-2 min-h-10 min-w-10 flex items-center justify-center cursor-pointer outline-none ring-0 focus:ring-0 focus:outline-none',
                  addButtonColor === currentParentBg && `border border-on-${addButtonColor}/50`,
                  getShapeClass(addButtonCornerRadius),
                  `bg-${addButtonColor} text-on-${addButtonColor} hover:bg-${addButtonColor}/90`
                )}
                onClick={handleAddButtonClick}
              >
                <MaterialIcon icon='add' size={20} className={`text-on-${addButtonColor}`} />
              </button>
            )}
          </div>
        </div>

        {showSearchResultsInfo && (
          <div
            className={cn('px-1 mb-4 text-sm', `text-on-${currentParentBg}/80`)}
          >{`Showing ${thousandFmtNoDecimal.format(filteredRowsCount)} results of ${thousandFmtNoDecimal.format(allRowsCount)}`}</div>
        )}

        {isLoading ? (
          <SkeletonGroup backgroundColor={currentParentBg!}>
            <div
              className='grid gap-4'
              style={{
                gridTemplateColumns: `repeat(${columnsCount}, 1fr)`,
              }}
            >
              {[...new Array(columnsCount * Math.min(numRows, 2))].map((_, i) => (
                <Skeleton key={i} className='w-full h-auto aspect-square rounded-md' />
              ))}
            </div>
          </SkeletonGroup>
        ) : groupEntries ? (
          <div className='flex flex-col gap-4'>
            {groupEntries.map(([k, groupRows]) => {
              return (
                <div key={k} className='flex flex-col gap-4'>
                  <div
                    className={cn(`text-on-${currentParentBg}`)}
                    style={{
                      ...groupHeaderTypoStyle,
                      ...groupHeaderMargin,
                      textAlign: groupHeaderAlign,
                    }}
                  >
                    <RenderText
                      colorType={groupHeaderColor}
                      colorSolid={groupHeaderColorSolid}
                      colorStartGradient={groupHeaderColorStartGradient}
                      colorEndGradient={groupHeaderColorEndGradient}
                      colorGradientDirection={groupHeaderColorGradientDirection}
                      value={k}
                      alignment={groupHeaderAlign}
                      backgroundColor={currentParentBg || 'neutral-100'}
                      limit={10}
                      typography={groupHeaderTypo}
                      className='w-full'
                    />
                  </div>
                  {children(getCompCardProps(groupRows))}
                </div>
              )
            })}
          </div>
        ) : (
          children(getCompCardProps(rows))
        )}

        {showAddButton && addButtonPosition === 'bottom' && (
          <div className='mt-4'>
            <Button
              colorVariant={addButtonColor}
              fullWidth
              onClick={handleAddButtonClick}
              icon={<MaterialIcon icon='add' size={20} />}
            >
              Add Item
            </Button>
          </div>
        )}

        {showPagination && (
          <div className={clsx('flex w-full py-4 pagination')} style={{ justifyContent: paginationAlign }}>
            <CompPagination
              totalPages={totalPages}
              perPage={pagination.perPage}
              page={pagination.page}
              onChange={setPagination}
              backgroundColor={currentParentBg}
            />
          </div>
        )}
      </BaseComponent>
    )
  }
)
CompCardWrapperCore.displayName = 'CompCardWrapperCore'
